/**
 * Authorised Domains & Tenancy Authentication Types
 * 
 * This module defines the data models for:
 * - Authorised email domains for tenancies
 * - SSO provider configuration
 * - Domain verification
 * - Join requests and invitations
 */

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

/**
 * Join policy determines how users with matching email domains are attached to tenancies
 */
export type JoinPolicy = 'AUTO_JOIN' | 'APPROVAL_REQUIRED' | 'INVITE_ONLY';

/**
 * Authentication mode determines which auth methods are available for a domain
 */
export type AuthMode = 'SSO_ONLY' | 'PREFER_SSO' | 'ANY';

/**
 * Default role for users joining via authorised domain
 * - POLICY_READER: user role (can read policies, limited access)
 * - PLATFORM_USER: owner role (full platform access)
 */
export type DefaultRole = 'POLICY_READER' | 'PLATFORM_USER';

/**
 * SSO provider type
 */
export type SSOProviderType = 'saml' | 'oidc';

/**
 * Join request status
 */
export type JoinRequestStatus = 'pending' | 'approved' | 'rejected';

/**
 * Invitation status
 */
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

/**
 * Domain verification status
 */
export type DomainVerificationStatus = 'unverified' | 'pending' | 'verified' | 'failed';

/**
 * Consumer email domains that cannot be registered as authorised domains
 */
export const CONSUMER_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'outlook.co.uk',
  'yahoo.com', 'ymail.com', 'yahoo.co.uk', 'yahoo.com.au',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me', 'pm.me',
  'zoho.com',
  'mail.com',
  'gmx.com', 'gmx.net', 'gmx.de',
  'yandex.com', 'yandex.ru',
  'qq.com',
  '163.com', '126.com',
  'sina.com',
  'fastmail.com', 'fastmail.fm',
  'tutanota.com', 'tutamail.com',
  'mailbox.org',
]);

/**
 * Check if a domain is a consumer domain
 */
export function isConsumerDomain(domain: string): boolean {
  return CONSUMER_DOMAINS.has(domain.toLowerCase());
}

// ============================================================================
// CORE INTERFACES
// ============================================================================

/**
 * Authorised domain configuration stored per tenancy
 */
export interface AuthorisedDomain {
  id: string;
  domain: string;
  verified: boolean;
  verificationStatus: DomainVerificationStatus;
  verificationToken?: string;
  verificationExpiresAt?: Timestamp;
  verificationAttempts: number;
  lastVerificationAttempt?: Timestamp;

  joinPolicy: JoinPolicy;
  authMode: AuthMode;
  defaultRole: DefaultRole;

  ssoProviderId?: string;

  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
  updatedBy?: string;
}

/**
 * SSO provider configuration stored per tenancy
 */
export interface SSOProvider {
  id: string;
  name: string;
  type: SSOProviderType;

  firebaseProviderId: string;

  displayName?: string;
  iconUrl?: string;

  // SAML Configuration
  samlConfig?: {
    idpEntityId: string;
    ssoUrl: string;
    x509Certificates: string[];
    signRequest?: boolean;
    requestBinding?: 'HTTP-POST' | 'HTTP-REDIRECT';
  };

  // OIDC Configuration
  oidcConfig?: {
    issuer: string;
    clientId: string;
    clientSecret?: string; // Stored encrypted
    responseType?: 'code' | 'id_token';
  };

  // Status
  configurationStatus: 'pending' | 'configured' | 'error';
  configurationError?: string;

  enabled: boolean;
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
  updatedBy?: string;
}

/**
 * Domain index entry for fast lookup across all tenancies
 * Stored at: system/authorisedDomains/domains/{domainHash}
 */
export interface DomainIndex {
  domain: string;
  domainHash: string;
  tenancyId: string;
  tenancyName?: string;
  verified: boolean;
  joinPolicy: JoinPolicy;
  authMode: AuthMode;
  defaultRole: DefaultRole;
  ssoProviderId?: string;
  firebaseProviderId?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Join request for users requesting access to a tenancy
 * Stored at: customers/{tenancyId}/joinRequests/{requestId}
 */
export interface JoinRequest {
  id: string;
  userId: string;
  email: string;
  domain: string;
  displayName?: string;
  status: JoinRequestStatus;
  reviewedBy?: string;
  reviewedAt?: Timestamp;
  rejectionReason?: string;
  createdAt: Timestamp;
}

/**
 * Invitation for users to join a tenancy
 * Stored at: customers/{tenancyId}/invitations/{inviteId}
 */
export interface Invitation {
  id: string;
  email: string;
  role?: string;
  invitedBy: string;
  invitedByName?: string;
  expiresAt: Timestamp;
  status: InvitationStatus;
  acceptedAt?: Timestamp;
  acceptedBy?: string;
  revokedAt?: Timestamp;
  revokedBy?: string;
  createdAt: Timestamp;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Request body for POST /auth/discover
 */
export interface DiscoverRequest {
  email: string;
}

/**
 * Response from POST /auth/discover
 */
export interface DiscoverResponse {
  found: boolean;
  tenancyId?: string;
  tenancyName?: string;
  joinPolicy?: JoinPolicy;
  authMode?: AuthMode;
  ssoProvider?: {
    id: string;
    name: string;
    type: SSOProviderType;
    firebaseProviderId: string;
    displayName?: string;
    iconUrl?: string;
  };
  availableAuthMethods: AuthMethod[];
  error?: string;
}

/**
 * Available authentication methods
 */
export type AuthMethod = 
  | 'password'
  | 'magic_link'
  | 'google'
  | 'microsoft'
  | 'saml'
  | 'oidc';

/**
 * Request body for POST /auth/postLogin
 */
export interface PostLoginRequest {
  idToken: string;
}

/**
 * Response from POST /auth/postLogin
 */
export interface PostLoginResponse {
  success: boolean;
  tenancyId?: string;
  tenancyName?: string;
  pendingApproval?: boolean;
  alreadyMember?: boolean;
  error?: string;
  errorCode?: PostLoginErrorCode;
}

/**
 * Error codes for postLogin
 */
export type PostLoginErrorCode =
  | 'NO_DOMAIN_MATCH'
  | 'DOMAIN_NOT_VERIFIED'
  | 'INVITE_REQUIRED'
  | 'EMAIL_NOT_VERIFIED'
  | 'INVALID_TOKEN'
  | 'INTERNAL_ERROR';

/**
 * Request body for POST /auth/linkSSO
 */
export interface LinkSSORequest {
  idToken: string;
  pendingCredential: string;
}

/**
 * Response from POST /auth/linkSSO
 */
export interface LinkSSOResponse {
  success: boolean;
  linkedProviderId?: string;
  error?: string;
}

/**
 * Request body for POST /auth/verifyDomain
 */
export interface VerifyDomainRequest {
  domainId: string;
}

/**
 * Response from POST /auth/verifyDomain
 */
export interface VerifyDomainResponse {
  success: boolean;
  verificationToken?: string;
  txtRecordName?: string;
  txtRecordValue?: string;
  expiresAt?: string;
  error?: string;
}

/**
 * Response from GET /auth/checkVerification
 */
export interface CheckVerificationResponse {
  verified: boolean;
  status: DomainVerificationStatus;
  lastChecked?: string;
  error?: string;
}

// ============================================================================
// ADMIN UI TYPES
// ============================================================================

/**
 * Form data for creating/editing an authorised domain
 */
export interface AuthorisedDomainFormData {
  domain: string;
  joinPolicy: JoinPolicy;
  authMode: AuthMode;
  defaultRole: DefaultRole;
  ssoProviderId?: string | null;
  firebaseProviderId?: string | null;
}

/**
 * Form data for creating/editing an SSO provider
 */
export interface SSOProviderFormData {
  name: string;
  type: SSOProviderType;
  firebaseProviderId?: string; // Generated by backend
  displayName?: string;
  iconUrl?: string;
  enabled: boolean;

  // SAML Configuration
  samlConfig?: {
    idpEntityId: string;
    ssoUrl: string;
    x509Certificates: string[]; // PEM format certificates
    signRequest?: boolean;
  };

  // OIDC Configuration
  oidcConfig?: {
    issuer: string;
    clientId: string;
    clientSecret?: string;
  };
}

/**
 * Domain with verification instructions
 */
export interface DomainWithVerification extends AuthorisedDomain {
  txtRecordName: string;
  txtRecordValue: string;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Extract domain from email address
 */
export function extractDomain(email: string): string | null {
  const normalized = email.toLowerCase().trim();
  const match = normalized.match(/@([a-z0-9.-]+\.[a-z]{2,})$/);
  return match ? match[1] : null;
}

/**
 * Normalize email address (lowercase, remove +tags for some domains)
 */
export function normalizeEmail(email: string): string {
  let normalized = email.toLowerCase().trim();
  
  const domain = extractDomain(normalized);
  if (!domain) return normalized;
  
  if (['gmail.com', 'googlemail.com'].includes(domain)) {
    const [localPart] = normalized.split('@');
    const cleanedLocal = localPart.split('+')[0].replace(/\./g, '');
    normalized = `${cleanedLocal}@gmail.com`;
  }
  
  return normalized;
}

/**
 * Generate a hash for domain indexing
 */
export function hashDomain(domain: string): string {
  const normalized = domain.toLowerCase().trim();
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36) + '_' + normalized.replace(/\./g, '_');
}

/**
 * Generate DNS TXT verification token
 */
export function generateVerificationToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = 'deiterate-verify=';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

/**
 * Get DNS TXT record name for domain verification
 */
export function getVerificationTxtRecordName(domain: string): string {
  return `_deiterate-verification.${domain}`;
}

// ============================================================================
// FIRESTORE COLLECTION PATHS
// ============================================================================

export const COLLECTIONS = {
  DOMAIN_INDEX: 'system/authorisedDomains/domains',
  getAuthorisedDomains: (tenancyId: string) => `customers/${tenancyId}/authorisedDomains`,
  getSSOProviders: (tenancyId: string) => `customers/${tenancyId}/ssoProviders`,
  getJoinRequests: (tenancyId: string) => `customers/${tenancyId}/joinRequests`,
  getInvitations: (tenancyId: string) => `customers/${tenancyId}/invitations`,
} as const;
